import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";

export type RegisterPayload = {
    googleToken?: string;
    msToken?: string;
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    teamName?: string;
    teamImageDataString?: string;
    phoneNumber?: string;
    jobTitle?: string;
};

type UpdateUserFieldsPayload = {
    firstName?: string;
    lastName?: string;
    jobTitle?: string;
};

type ChangePasswordPayload = {
    existingPassword: string;
    newPassword: string;
};

type VerifyTokenResponse = {
    valid: boolean;
    err?: string;
    accessToken?: string;
    email: string;
    invite?: string;
    firstName?: string;
    lastName?: string;
};

export type PendingInvite = {
    id: number;
    teamName: string;
    teamId: number;
    invitedByUser: {
        id: number;
        first_name: string;
        last_name: string;
    } | null;
    createdAt: string;
};

type PendingInvitesResponse = {
    invites: PendingInvite[];
};

type AcceptInviteResponse = {
    success: boolean;
    teamId?: number;
    teamName?: string;
    error?: string;
};

type DeclineInviteResponse = {
    success: boolean;
    error?: string;
};

export interface User {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    createdAt: string;
    lastTeam: Team;
    userTeams: {
        userId: number;
        teamId: number;
        team: Team;
    }[];
    iconBackgroundColorClass: string;
    staffRoles: string[];
    emailSignature: string | null;
    plan: {
        usage: {
            ProcessDocument: number;
            RFQsSent: number;
            RevealProduct: number;
            RevealSupplier: number;
            InviteTeamMember: number;
            VendorSearch: number;
            TotalTrialActions: number;
        };
        limits: {
            maxDocumentsProcessed: number;
            maxRFQsSent: number;
            maxProductsRevealed: number;
            maxSuppliersRevealed: number;
            maxTeamSize: number;
            maxActionCount: number;
        };
        gates: {
            canViewPremiumSupplierData: boolean;
            canBulkPriceAnalyze: boolean;
        };
        currentMarketingName: string;
        shortMarketingName: string;
        nextPlanUp: {
            marketingName: string;
            limits: {
                maxDocumentsProcessed: number;
                maxRFQsSent: number;
                maxProductsRevealed: number;
                maxSuppliersRevealed: number;
                maxTeamSize: number;
            };
            gates: {
                canViewPremiumSupplierData: boolean;
                canBulkPriceAnalyze: boolean;
            };
        } | null;
    };
    featureFlags: Record<string, string | boolean>;
}

export interface Team {
    id: number;
    name: string;
    stripeCustomerId: string;
    // logoImageFilename: string | null;
    logoUrl?: string;
    createdAt: string;
    billingTier: string;
    billingCycleStartDate: string;
    disabledFeatures?: string[];
}

export default {
    getCurrentUser: (): Promise<{ statusCode: number; user: User | null }> =>
        fetch(`${PURCHASER_API_BASE_URL}/me`, { headers: getHeaders() })
            .then(res => {
                if (res.status !== 200) return { statusCode: res.status, user: null };
                // if this response includes a new token, overwrite the existing one
                if (res.headers.get("X-Auth-Token")) localStorage.setItem("token", res.headers.get("X-Auth-Token") as string);
                return res.json().then(json => ({ statusCode: res.status, user: json }));
            })
            .catch(err => {
                return { statusCode: 0, user: null };
            }),

    registerUser: (params: RegisterPayload): Promise<{ error?: string; accessToken?: string }> =>
        fetch(`${PURCHASER_API_BASE_URL}/register`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(params),
        }).then(res => res.json()),

    loginUser: (email?: string, password?: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/login`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ email, password }),
        }).then(res => res.json()),

    loginUserWithToken: (token: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/login/with-token`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ token }),
        }).then(res => res.json()),

    switchTeam: (teamId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/switch-team/${teamId}`, { headers: getHeaders() }).then(res => {
            if (res.headers.get("X-Auth-Token")) localStorage.setItem("token", res.headers.get("X-Auth-Token")!);
            return res.json();
        }),

    updateUserFields: (payload: UpdateUserFieldsPayload) =>
        fetch(`${PURCHASER_API_BASE_URL}/user`, {
            headers: getHeaders(),
            method: "PUT",
            body: JSON.stringify({ ...payload, companyName: null }),
        }).then(res => {
            // if this response includes a new token, overwrite the existing one
            if (res.headers.get("X-Auth-Token"))
                // We literally just checked if the token is null, so we can safely use ! here
                localStorage.setItem("token", res.headers.get("X-Auth-Token")!);

            return res.json();
        }),

    sendPasswordReset: (email: string, sendOTP: boolean) =>
        fetch(`${PURCHASER_API_BASE_URL}/set-new-password/send-token`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ email, sendOTP }),
        }).then(res => res.json()),

    resetPassword: (token: string, newPassword: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/set-new-password/with-token`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ token, newPassword }),
        }).then(res => {
            if (res.status !== 200) {
                return Promise.reject();
            }
            return res.json();
        }),

    verifyResetToken: (token: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/verify-reset-token`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ token }),
        }).then(res => {
            return res.json();
        }),

    changePassword: ({ existingPassword, newPassword }: ChangePasswordPayload) =>
        fetch(`${PURCHASER_API_BASE_URL}/set-new-password/authenticated-user`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ existingPassword, newPassword }),
        }).then(res => res.json()),

    getEmailDomainMetadata: (domain: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/register/domain-metadata?domain=${domain}`, { headers: getHeaders() }).then(res => res.json()),

    verifyGoogleToken: (idToken: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/register/verify-google-token`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ idToken }),
        }).then(res => res.json()) as Promise<VerifyTokenResponse>,

    verifyMicrosoftToken: (idToken: string) =>
        fetch(`${PURCHASER_API_BASE_URL}/register/verify-ms-token`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ idToken }),
        }).then(res => res.json()) as Promise<VerifyTokenResponse>,

    emailVerification: async (
        email: string,
    ): Promise<{ invite?: string; exists: boolean; domain: string; isFree: boolean; verificationStarted: boolean }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/register/email-verification`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ email }),
        });

        if (res.status === 200) {
            return await res.json();
        } else {
            return Promise.reject("Invalid response");
        }
    },

    verifyEmailCode: async (email: string, code: string): Promise<boolean> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/register/verify-email-code`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ email, code }),
        });

        if (res.status === 200) {
            return true;
        }

        return false;
    },

    resendVerifyEmail: async (email: string) => {
        return await fetch(`${PURCHASER_API_BASE_URL}/register/resend-email-verification`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ email }),
        });
    },

    updateEmailSignature: (html: string | null) => {
        return fetch(`${PURCHASER_API_BASE_URL}/email-signature`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ emailSignature: html }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update email signature");
            }
        });
    },
    clearEmailSignature: () => {
        return fetch(`${PURCHASER_API_BASE_URL}/email-signature`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ emailSignature: null }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Failed to update email signature");
            }
        });
    },
    getHelpArticles: () => {
        return fetch(`https://purchaser.ai/api/featured-docs`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },
    getNewFeatures: () => {
        return fetch(`https://purchaser.ai/api/new-features`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    // Pending team invites for current user
    getPendingInvites: (): Promise<PendingInvitesResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/user/pending-invites`, {
            headers: getHeaders(),
        }).then(res => res.json()),

    acceptInvite: (inviteId: number): Promise<AcceptInviteResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/user/pending-invites/${inviteId}/accept`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({}),
        }).then(res => res.json()),

    declineInvite: (inviteId: number): Promise<DeclineInviteResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/user/pending-invites/${inviteId}`, {
            headers: getHeaders(),
            method: "DELETE",
            body: JSON.stringify({}),
        }).then(res => res.json()),
};
